import { useState, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { getUserDetails } from 'services/user.service';
import { useActiveStore } from 'context/activeStoreProvider';
import { useAccountAccess } from 'context/accountAccessProvider';
import { useRolePermissions } from 'hooks/useRolePermissions';
import { useReturnsEnabled } from 'hooks/useReturnsEnabled';
import { useOutstationEnabled } from 'hooks/useOutstationEnabled';
import { useLiveFeatureSubscriptions } from 'hooks/useLiveFeatureSubscriptions';
import { canAccessSection } from 'utils/canAccessSection';
import { SECTIONS } from 'utils/roles';
import {
  NAV_ICON_KEYS,
  getDashboardIconKey,
  getOrdersIconKey,
  getProjectsIconKey,
} from 'utils/navigationIcons';
import { FEATURE_KEYS } from 'utils/billingFeatures.constants';
import { useSubscriptionEnabled } from 'hooks/useSubscriptionEnabled';
import { buildChoosePlanPath } from 'utils/choosePlanPath';
import { MARKETING_VIEWS } from 'utils/marketing-views';
import { IS_WALLET_ENABLED } from 'utils/walletFormat';
import { GIFT_CARDS_PAGE_TABS } from 'hooks/useGiftCardsPageLinks';
import { LOYALTY_COUPONS_TAB } from 'utils/loyaltyTabs';
import { REWARD_KEYS } from 'utils/rewardKeys';
import { VOICE_PAGE_TABS } from 'utils/voiceTabs';

export const MOBILE_PRIMARY_NAV_COUNT = 5;

export function useNavigationMenuItems() {
  const [userDetails, setUserDetails] = useState({});
  const route = useRouter();
  const { userId, businessAccountId } = route.query;
  const { data: session, update } = useSession();
  const { activeStore } = useActiveStore();
  // Billing, and buying a plan, belong to the account owner.
  const { canSeeBilling, isLoading: isAccountAccessLoading } = useAccountAccess();

  const { rolePermissions: livePermissions } = useRolePermissions() || {};
  const liveFeatureSubscriptions = useLiveFeatureSubscriptions(
    userId || session?.user?.id
  );

  const role = session?.user?.role;
  const rolePermissions =
    livePermissions || session?.user?.rolePermissions || null;
  const can = (section) => canAccessSection(section, role, rolePermissions);
  // Live access wins over the session's copy for the same reason live
  // permissions do above: the JWT caches featureSubscriptions for a full day,
  // so a plan activated mid-session would leave Loyalty and Marketing reading
  // "(Locked)" here until the next sign-in — while their pages, gated by
  // utils/requireFeatureAccess.js, let the user straight in.
  const featureSubscriptions =
    liveFeatureSubscriptions ||
    session?.user?.subscription?.featureSubscriptions ||
    {};
  // Local-dev-only escape hatch: set NEXT_PUBLIC_BYPASS_FEATURE_GATES=true in
  // afto_ui/.env.local (gitignored, per-developer) to see Marketing/Loyalty/
  // Website as unlocked without a real Stripe subscription. Never set outside
  // a developer's own machine — this only ever reads `true` if that file exists.
  const hasFeatureAccess = (featureKey) =>
    process.env.NEXT_PUBLIC_BYPASS_FEATURE_GATES === 'true' ||
    !!featureSubscriptions[featureKey]?.hasActiveSubscription;

  const isMainParentStore =
    session?.user?.storeType === 'multi_store_parent' &&
    activeStore?.isParent !== false;

  const isEsIndexPresent = Boolean(session?.user?.elasticIndexName);
  const { isSubscriptionEnabled } = useSubscriptionEnabled();

  const canShowReturnsSection =
    can(SECTIONS.RETURNS) &&
    !(session?.user?.isLegal || session?.user?.isDental);
  const { returnsEnabled } = useReturnsEnabled(canShowReturnsSection);
  // Returns also depends on outstation shipping being on — the return flow ships labels through it.
  const { outstationEnabled } = useOutstationEnabled(canShowReturnsSection);

  useEffect(() => {
    async function init() {
      try {
        const response = await getUserDetails(userId || session?.user?.id);

        session.user.hasPaidAccessByAdmin =
          response?.entity?.hasPaidAccessByAdmin;
        session.user.subscription.paymentServiceUserId =
          response?.entity?.businessAccount?.paymentServiceUserId;
        session.user.isLegal =
          response?.entity?.businessAccount?.industryDetails?.index == 12;
        session.user.isDental =
          response?.entity?.businessAccount?.industryDetails?.index == 13;
        // Note: this used to force a redirect to /billing app-wide when the
        // business had no active subscription. Loyalty and Marketing Automation
        // are now optional paid add-ons — every other section is free, so that
        // whole-app paywall no longer applies. Access to those two sections
        // specifically is gated at the API layer and via their own nav lock state.
        update(session);
        setUserDetails(response.entity);
      } catch (error) {
        console.error('Error fetching user details:', error);
      }
    }
    if (userId || session?.user?.id) init();
  }, [userId, session?.user?.id]);

  const appendUserParams = (url) =>
    userId
      ? `${url}?userId=${userId}&businessAccountId=${businessAccountId}`
      : url;

  // Where a "(Locked)" item leads: the plan picker, already on that feature.
  // These used to point at /billing, which opens on the Stripe Connect tab —
  // three clicks and an unrelated screen away from the plan being asked for.
  //
  // A multi-store parent keeps /billing: its setup flow has no plan step, so
  // the plan-picker link would land it on Business Details instead.
  //
  // Anyone but the owner gets /billing as well, where they are told the owner
  // is the one who can unlock it: the plan picker would only refuse them.
  const isKnownNonOwner = !isAccountAccessLoading && !canSeeBilling;
  const lockedFeatureHref = (featureKey) =>
    isMainParentStore || isKnownNonOwner
      ? appendUserParams('/billing')
      : buildChoosePlanPath({ featureKey, userId, businessAccountId });

  // A link to one tab of a page whose tabs live in ?tab=, keeping the admin's userId params.
  const withTab = (path, tab) => `${appendUserParams(path)}${userId ? '&' : '?'}tab=${tab}`;

  // Loyalty, Coupons, Gift cards and Customer wallets under one Rewards dropdown. Each keeps its
  // own access rule; with a single one left it stays a plain link, and with none Rewards is hidden.
  // `rewardKey` and `isLocked` let the agent's Rewards quick start reuse these exact links and rules.
  const buildRewardsItems = () => {
    const tab = route.query.tab;
    const onLoyalty = route.route.startsWith('/loyalty');
    const onGiftCards = route.route.startsWith('/gift-cards');
    const children = [];

    if (can(SECTIONS.LOYALTY)) {
      const isUnlocked = hasFeatureAccess(FEATURE_KEYS.LOYALTY);
      const lockedHref = lockedFeatureHref(FEATURE_KEYS.LOYALTY);
      children.push(
        {
          href: isUnlocked ? appendUserParams('/loyalty') : lockedHref,
          iconKey: NAV_ICON_KEYS.loyalty,
          name: isUnlocked ? 'Loyalty' : 'Loyalty (Locked)',
          active: onLoyalty && tab !== LOYALTY_COUPONS_TAB,
          disabled: false,
          toolTip: !isUnlocked,
          toolTipText: 'Subscribe to Loyalty to unlock',
          rewardKey: REWARD_KEYS.LOYALTY,
          isLocked: !isUnlocked,
        },
        {
          // Coupons are a tab of the Loyalty page, so they unlock with Loyalty.
          href: isUnlocked ? withTab('/loyalty', LOYALTY_COUPONS_TAB) : lockedHref,
          iconKey: NAV_ICON_KEYS.loyalty,
          name: isUnlocked ? 'Coupons' : 'Coupons (Locked)',
          active: onLoyalty && tab === LOYALTY_COUPONS_TAB,
          disabled: false,
          toolTip: !isUnlocked,
          toolTipText: 'Subscribe to Loyalty to unlock',
          rewardKey: REWARD_KEYS.COUPONS,
          isLocked: !isUnlocked,
        },
      );
    }

    if (IS_WALLET_ENABLED && can(SECTIONS.GIFT_CARDS) && !(session?.user?.isLegal || session?.user?.isDental)) {
      children.push(
        {
          href: appendUserParams('/gift-cards'),
          iconKey: NAV_ICON_KEYS.giftCards,
          name: 'Gift cards',
          active: onGiftCards && tab !== GIFT_CARDS_PAGE_TABS.WALLETS,
          disabled: false,
          rewardKey: REWARD_KEYS.GIFT_CARDS,
          isLocked: false,
        },
        {
          href: withTab('/gift-cards', GIFT_CARDS_PAGE_TABS.WALLETS),
          iconKey: NAV_ICON_KEYS.giftCards,
          name: 'Customer wallets',
          active: onGiftCards && tab === GIFT_CARDS_PAGE_TABS.WALLETS,
          disabled: false,
          rewardKey: REWARD_KEYS.WALLETS,
          isLocked: false,
        },
      );
    }

    if (children.length === 0) return [];
    if (children.length === 1) return children;
    return [
      {
        href: children[0].href,
        iconKey: NAV_ICON_KEYS.rewards,
        name: 'Rewards',
        active: onLoyalty || onGiftCards,
        disabled: false,
        children,
      },
    ];
  };

  // The phone assistant: its settings, and the calls it answered with their transcripts.
  const buildVoiceItems = () => {
    if (!can(SECTIONS.VOICE) || session?.user?.isLegal || session?.user?.isDental) return [];
    const onVoice = route.route.startsWith('/voice');
    const onTranscriptions = onVoice && route.query.tab === VOICE_PAGE_TABS.TRANSCRIPTIONS;
    return [
      {
        href: withTab('/voice', VOICE_PAGE_TABS.SETTINGS),
        iconKey: NAV_ICON_KEYS.voice,
        name: 'Voice',
        active: onVoice,
        disabled: false,
        children: [
          {
            href: withTab('/voice', VOICE_PAGE_TABS.SETTINGS),
            name: 'Settings',
            active: onVoice && !onTranscriptions,
          },
          {
            href: withTab('/voice', VOICE_PAGE_TABS.TRANSCRIPTIONS),
            name: 'Transcriptions',
            active: onTranscriptions,
          },
        ],
      },
    ];
  };

  const menuItems = useMemo(() => {
    let items = [
      {
        href: userId
          ? `/?userId=${userId}&businessAccountId=${businessAccountId}`
          : '/',
        iconKey: getDashboardIconKey({
          industry: userDetails?.businessAccount?.industry,
          isLegal: session?.user?.isLegal,
          isDental: session?.user?.isDental,
        }),
        name:
          userDetails?.businessAccount?.industry === 'Manufacturing'
            ? 'Sales Dashboard'
            : session?.user?.isLegal || session?.user?.isDental
            ? 'Dashboard'
            : 'Store Dashboard',
        active: route.route === '/',
        disabled: false,
      },
      {
        href: userId
          ? `/customers?userId=${userId}&businessAccountId=${businessAccountId}`
          : '/customers',
        iconKey: NAV_ICON_KEYS.customers,
        name: 'Customers',
        active: route.route === '/customers',
        disabled: false,
      },
      ...(canShowReturnsSection && returnsEnabled && outstationEnabled
        ? [
            {
              href: userId
                ? `/returns?userId=${userId}&businessAccountId=${businessAccountId}`
                : '/returns',
              iconKey: NAV_ICON_KEYS.returns,
              name: 'Returns',
              active: route.route.startsWith('/returns'),
              disabled: false,
            },
          ]
        : []),
      ...(can(SECTIONS.CAMPAIGNS)
        ? [
            (() => {
              const isUnlocked = hasFeatureAccess(
                FEATURE_KEYS.MARKETING_AUTOMATION
              );
              const campaignsHref = userId
                ? `/campaign?userId=${userId}&businessAccountId=${businessAccountId}`
                : '/campaign';
              const viewHref = (view) =>
                userId
                  ? `${campaignsHref}&view=${view}`
                  : `/campaign?view=${view}`;
              // The views are query params on /campaign, so route.route alone
              // cannot tell them apart.
              const activeView = route.query.view;
              const onCampaigns = route.route.includes('/campaign');

              return {
                href: isUnlocked
                  ? campaignsHref
                  : lockedFeatureHref(FEATURE_KEYS.MARKETING_AUTOMATION),
                iconKey: NAV_ICON_KEYS.marketing,
                name: isUnlocked ? 'Marketing' : 'Marketing (Locked)',
                active: onCampaigns,
                disabled: false,
                toolTip: !isUnlocked,
                toolTipText:
                  'Upgrade to Starter or higher to unlock Marketing Automation',
                // Left off while locked: the parent sends you to the plan
                // picker, and child links would route straight past that.
                children: isUnlocked
                  ? [
                      {
                        href: campaignsHref,
                        name: 'Campaigns',
                        active: onCampaigns && !activeView,
                      },
                      {
                        href: viewHref(MARKETING_VIEWS.calendar),
                        name: 'Calendar',
                        active: activeView === MARKETING_VIEWS.calendar,
                      },
                      {
                        href: viewHref(MARKETING_VIEWS.website),
                        name: 'Web Banners',
                        active: activeView === MARKETING_VIEWS.website,
                      },
                    ]
                  : undefined,
              };
            })(),
          ]
        : []),
      ...buildRewardsItems(),
      ...(can(SECTIONS.CHANNELS) &&
      !(session?.user?.isLegal || session?.user?.isDental)
        ? [
            {
              href: userId
                ? `/collections?userId=${userId}&businessAccountId=${businessAccountId}`
                : '/collections',
              iconKey: NAV_ICON_KEYS.channels,
              name: 'Collections',
              active: route.route === '/collections',
              disabled: false,
            },
          ]
        : []),
      ...buildVoiceItems(),
      ...(isSubscriptionEnabled
        ? [
            {
              href: appendUserParams('/subscription/deliveries'),
              iconKey: NAV_ICON_KEYS.calendar,
              name: 'Subscription',
              active: route.route.startsWith('/subscription'),
              disabled: false,
              // Same dropdown pattern as Inventory, ordered the way the work runs: set the
              // week's Menus, see who is on a box, prepare the Deliveries, then drive them out
              // on a Delivery Route. Plans sits last — it is the setup a merchant touches once
              // and then rarely again.
              children: [
                {
                  href: appendUserParams('/subscription/menus'),
                  name: 'Menus',
                  active: route.route === '/subscription/menus',
                },
                {
                  href: appendUserParams('/subscription/subscribers'),
                  name: 'Subscribers',
                  active: route.route.startsWith('/subscription/subscribers'),
                },
                {
                  href: appendUserParams('/subscription/deliveries'),
                  name: 'Deliveries',
                  active: route.route === '/subscription/deliveries',
                },
                {
                  // Straight after Deliveries: a box is packed on that board, and this is where
                  // the packed ones get driven out.
                  href: appendUserParams('/subscription/delivery-route'),
                  name: 'Delivery Route',
                  active: route.route === '/subscription/delivery-route',
                },
                {
                  href: appendUserParams('/subscription/plans'),
                  name: 'Plans',
                  active: route.route === '/subscription/plans',
                },
              ],
            },
          ]
        : []),
      ...(can(SECTIONS.INVENTORY)
        ? [
            {
              href: appendUserParams('/Inventory'),
              iconKey: session?.user?.isLegal
                ? NAV_ICON_KEYS.annotate
                : NAV_ICON_KEYS.inventory,
              name: session?.user?.isLegal ? 'Annotate' : 'Inventory',
              active: route.route === '/Inventory',
              disabled: false,
              // Sub-pages surface as a dropdown here rather than as more buttons in the
              // (already busy) Inventory toolbar. Not shown for the legal "Annotate" variant.
              children: session?.user?.isLegal
                ? undefined
                : [
                    {
                      href: appendUserParams('/Inventory'),
                      name: 'Products',
                      active: route.route === '/Inventory',
                    },
                    {
                      href: appendUserParams('/Inventory/categories'),
                      name: 'Categories',
                      active: route.route === '/Inventory/categories',
                    },
                    {
                      href: appendUserParams('/Inventory/option-groups'),
                      name: 'Option Groups',
                      active: route.route === '/Inventory/option-groups',
                    },
                  ],
            },
          ]
        : []),
      ...(can(SECTIONS.REPORTS)
        ? [
            {
              href: userId
                ? `/reports?userId=${userId}&businessAccountId=${businessAccountId}`
                : '/reports',
              iconKey: NAV_ICON_KEYS.reports,
              name: 'Reports',
              active: route.route === '/reports',
              disabled: false,
            },
          ]
        : []),
      ...(session?.user?.isLegal || session?.user?.isDental || isEsIndexPresent
        ? [
            {
              href: userId
                ? `/Dashboard?userId=${userId}&businessAccountId=${businessAccountId}`
                : '/Dashboard',
              iconKey: getOrdersIconKey({
                industry: userDetails?.businessAccount?.industry,
                isLegal: session?.user?.isLegal,
                isDental: session?.user?.isDental,
              }),
              name:
                userDetails?.businessAccount?.industry === 'Manufacturing'
                  ? 'Orders'
                  : session?.user?.isLegal || session?.user?.isDental
                  ? 'Files'
                  : 'Invoices',
              active: route.route === '/Dashboard',
              disabled: false,
            },
          ]
        : []),
      ...(session?.user?.isLegal || session?.user?.isDental || isEsIndexPresent
        ? [
            {
              href: userId
                ? `/projects?userId=${userId}&businessAccountId=${businessAccountId}`
                : '/projects',
              iconKey: getProjectsIconKey({
                industry: userDetails?.businessAccount?.industry,
              }),
              name:
                userDetails?.businessAccount?.industry === 'Manufacturing'
                  ? 'Stores'
                  : session?.user?.isLegal || session?.user?.isDental
                  ? 'Clients'
                  : 'Vendors',
              active:
                route.route.includes('/projects') &&
                route.route.split('/').length < 5,
              disabled: false,
            },
          ]
        : []),
      ...(session?.user?.isLegal || session?.user?.isDental || isEsIndexPresent
        ? [
            {
              href: userId
                ? `/event-types?userId=${userId}&businessAccountId=${businessAccountId}`
                : '/event-types',
              iconKey: NAV_ICON_KEYS.eventTypes,
              name: 'Event Types',
              active: route.route === '/event-types',
              disabled: false,
            },
          ]
        : []),
      ...((session?.user?.isLegal ||
        session?.user?.isDental ||
        isEsIndexPresent) &&
      can(SECTIONS.BOOKINGS)
        ? [
            {
              href: userId
                ? `/bookings?userId=${userId}&businessAccountId=${businessAccountId}`
                : '/bookings',
              iconKey: NAV_ICON_KEYS.bookings,
              name: 'Bookings',
              active: route.route === '/bookings',
              disabled: false,
            },
          ]
        : []),
      ...((session?.user?.isLegal ||
        session?.user?.isDental ||
        isEsIndexPresent) &&
      can(SECTIONS.BOOKINGS)
        ? [
            {
              href: userId
                ? `/availability?userId=${userId}&businessAccountId=${businessAccountId}`
                : '/availability',
              iconKey: NAV_ICON_KEYS.availability,
              name: 'Availability',
              active: route.route === '/availability',
              disabled: false,
            },
          ]
        : []),
      ...((session?.user?.isLegal ||
        session?.user?.isDental ||
        isEsIndexPresent) &&
      can(SECTIONS.CALENDAR)
        ? [
            {
              href: userId
                ? `/calendar?userId=${userId}&businessAccountId=${businessAccountId}`
                : '/calendar',
              iconKey: NAV_ICON_KEYS.calendar,
              name: 'Calendar',
              active: route.route === '/calendar',
              disabled: false,
            },
          ]
        : []),
      ...(session?.user?.isDental && can(SECTIONS.WEBSITE)
        ? [
            (() => {
              const isUnlocked = hasFeatureAccess(FEATURE_KEYS.WEBSITE);
              return {
                href: isUnlocked
                  ? userId
                    ? `/website?userId=${userId}&businessAccountId=${businessAccountId}`
                    : '/website'
                  : lockedFeatureHref(FEATURE_KEYS.WEBSITE),
                iconKey: NAV_ICON_KEYS.website,
                name: isUnlocked ? 'Website' : 'Website (Locked)',
                active: route.route === '/website',
                disabled: false,
                toolTip: !isUnlocked,
                toolTipText:
                  'Your 14-day free Website trial has ended — subscribe to a paid plan to unlock',
              };
            })(),
          ]
        : []),
      // Every logged-in business owner, no section gate — same shape as
      // Dashboard/Customers above, not restricted like the dental-only
      // 'website' item. It still needs the Website subscription, so it locks
      // like Loyalty and Marketing rather than bouncing to /billing unexplained.
      (() => {
        const isUnlocked = hasFeatureAccess(FEATURE_KEYS.WEBSITE);
        return {
          href: isUnlocked
            ? userId
              ? `/studio?userId=${userId}&businessAccountId=${businessAccountId}`
              : '/studio'
            : lockedFeatureHref(FEATURE_KEYS.WEBSITE),
          iconKey: NAV_ICON_KEYS.website,
          name: isUnlocked ? 'Website Builder' : 'Website Builder (Locked)',
          active: route.route === '/studio',
          disabled: false,
          toolTip: !isUnlocked,
          toolTipText:
            'Your 14-day free Website trial has ended — subscribe to a paid plan to unlock',
        };
      })(),
      ...(session?.user?.isLegal || session?.user?.isDental || isEsIndexPresent
        ? [
            {
              href: userId
                ? `/calendar-settings?userId=${userId}&businessAccountId=${businessAccountId}`
                : '/calendar-settings',
              iconKey: NAV_ICON_KEYS.calendarSettings,
              name: 'Calendar Settings',
              active: route.route === '/calendar-settings',
              disabled: false,
            },
          ]
        : []),
      ...(session?.user?.isDental && can(SECTIONS.STAFF_MANAGEMENT)
        ? [
            {
              href: userId
                ? `/doctors?userId=${userId}&businessAccountId=${businessAccountId}`
                : '/doctors',
              iconKey: NAV_ICON_KEYS.doctors,
              name: 'Doctors',
              active: route.route === '/doctors',
              disabled: false,
            },
          ]
        : []),
      ...(session?.user?.isDental
        ? [
            {
              href: userId
                ? `/chatbot?userId=${userId}&businessAccountId=${businessAccountId}`
                : '/chatbot',
              iconKey: NAV_ICON_KEYS.serviceAssistant,
              name: 'Service Assistant',
              active: route.route === '/chatbot',
              disabled: false,
            },
          ]
        : []),
      ...(session?.user?.isLegal || session?.user?.isDental || isEsIndexPresent
        ? [
            {
              href: userId
                ? `/purchaseOrder?userId=${userId}&businessAccountId=${businessAccountId}`
                : session?.user?.isLegal || session?.user?.isDental
                ? '/dataviz'
                : '/purchaseOrder',
              iconKey:
                session?.user?.isLegal || session?.user?.isDental
                  ? NAV_ICON_KEYS.dataViz
                  : NAV_ICON_KEYS.create,
              name:
                session?.user?.isLegal || session?.user?.isDental
                  ? 'Data Viz'
                  : 'Create',
              active: route.route.includes('/purchaseOrder'),
              disabled: false,
            },
          ]
        : []),
      ...(route.route.includes('/projects') &&
      route.route.split('/').length >= 5
        ? [
            {
              href: '/review',
              iconKey: NAV_ICON_KEYS.review,
              name: 'Review',
              active:
                route.route.includes('/projects') &&
                route.route.split('/').length >= 5,
              disabled: true,
            },
          ]
        : []),
      ...(route.route === '/verify-invoice'
        ? [
            {
              href: '/annotate',
              iconKey: NAV_ICON_KEYS.verify,
              name: 'Verify',
              active: route.route === '/verify-invoice',
              disabled: false,
            },
          ]
        : []),
      ...(session?.user?.isLegal || session?.user?.isDental || isEsIndexPresent
        ? [
            {
              href: '/dataviz',
              iconKey: NAV_ICON_KEYS.dataViz,
              name: 'Data viz',
              active: route.route === '/dataviz',
              disabled: false,
            },
          ]
        : []),
    ];

    if (session?.user?.isLegal || session?.user?.isDental) {
      items = items.slice(0, 18);
    }

    if (isMainParentStore) {
      items = items.filter(
        (item) =>
          !item.href?.includes('/Inventory') &&
          !item.href?.includes('/Dashboard') &&
          !item.href?.includes('/orders')
      );
    }

    return items;
  }, [
    isKnownNonOwner,
    userId,
    businessAccountId,
    userDetails,
    session,
    route.route,
    route.query.view,
    // Rewards highlights Coupons and Customer wallets from ?tab=.
    route.query.tab,
    isEsIndexPresent,
    isMainParentStore,
    rolePermissions,
    role,
    liveFeatureSubscriptions,
    isSubscriptionEnabled,
    canShowReturnsSection,
    returnsEnabled,
    outstationEnabled,
  ]);

  const footerItems = useMemo(() => {
    const items = [];

    // Ownership, not the billing section of a role: every invited admin
    // holds the same role as a single store's founder.
    if (canSeeBilling) {
      items.push({
        href: appendUserParams('/billing'),
        iconKey: NAV_ICON_KEYS.billing,
        name: 'Billing',
        active: route.route.includes('/billing'),
        disabled: false,
      });
    }

    if (!(session?.user?.isLegal || session?.user?.isDental)) {
      items.push({
        href: appendUserParams('/APIDocumentation'),
        iconKey: NAV_ICON_KEYS.apiDoc,
        name: 'API Doc',
        active: route.route.includes('/APIDocumentation'),
        disabled: false,
      });
    }

    items.push({
      href: appendUserParams('/help'),
      iconKey: NAV_ICON_KEYS.help,
      name: 'Help',
      active: route.route.includes('/help'),
      disabled: false,
    });

    items.push({
      href: appendUserParams('/settings'),
      iconKey: NAV_ICON_KEYS.settings,
      name: 'Settings',
      active: route.route.includes('/settings'),
      disabled: false,
    });

    return items;
  }, [session, route.route, userId, businessAccountId, rolePermissions, role, canSeeBilling]);

  return { menuItems, footerItems, appendUserParams };
}
